import { useState } from 'react';
import { Button, Card, Cell, Icon, InputText, Spacer, Text, Validation } from '@vinted/web-ui';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'components/@support';
import FaqEntryUrl from 'components/FaqEntryUrl';
import SeparatedList from 'components/SeparatedList';
import useSuccessUrl from 'components/Auth/hooks/useSuccessUrl';
import { AuthenticateGrantType } from 'constants/auth';
import { FaqEntryType } from 'constants/faq-entry';
import { AccessChannel } from 'constants/index';
import { CODE_LOGIN, FORGOT_PASSWORD_URL, ROOT_URL } from 'constants/routes';
import { authenticateUser } from 'data/api/account';
import { getFingerprint } from 'libs/utils/fingerprint';
import { getRefUrl } from 'libs/utils/url';
import { navigateToPage } from 'libs/utils/window';
import { ResponseCode } from 'data/api/response-codes';
import useTranslate from 'hooks/useTranslate';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const PasswordVerificationLogin = _ref => {
  let {
    email,
    idToken
  } = _ref;
  const translate = useTranslate('user.wrong_email_in_registration');
  const successUrl = useSuccessUrl();
  const isLoginWithoutEmailInModalEnabled = useSelector(getIsFeatureSwitchEnabled('login_without_email_in_modal'));
  const {
    0: password,
    1: setPassword
  } = useState('');
  const {
    0: validationErrors,
    1: setValidationErrors
  } = useState([]);
  const {
    0: isPasswordVisible,
    1: setIsPasswordVisible
  } = useState(false);

  const doAuthenticate = async () => {
    if (!email) return;
    const fingerprint = await getFingerprint();
    const response = await authenticateUser({
      grantType: AuthenticateGrantType.Password,
      username: email,
      controlCode: idToken,
      password,
      fingerprint
    });

    if (response.code === ResponseCode.Required2FA && 'payload' in response && response.payload) {
      navigateToPage(CODE_LOGIN({
        refUrl: successUrl,
        authProps: response.payload
      }));
      return;
    }

    if ('errors' in response) {
      setValidationErrors([response.message]);
      return;
    }

    navigateToPage(getRefUrl(ROOT_URL));
  };

  const handleInputChange = event => {
    setPassword(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(prevState => !prevState);
  };

  const renderLink = (url, text) => /*#__PURE__*/_jsx("a", {
    href: url,
    children: /*#__PURE__*/_jsx(Text, {
      bold: true,
      text: text,
      clickable: true
    })
  });

  const renderContactUsLink = url => renderLink(url, translate('contact_us'));

  const renderPasswordResetLink = url => renderLink(url, translate('reset_password'));

  const errorMessage = validationErrors.length ? /*#__PURE__*/_jsx(Validation, {
    text: validationErrors[0],
    theme: "warning"
  }) : null;
  const passwordIconName = isPasswordVisible ? 'eye-denied' : 'eye';

  const renderContent = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Cell, {
      children: [/*#__PURE__*/_jsx(Text, {
        text: translate('heading', {
          registration_email: email
        }),
        alignment: Text.Alignment.Center,
        width: Text.Width.Parent,
        type: Text.Type.Heading
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.X2Large
      }), /*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        id: "user.wrong_email_in_registration.error_explanation",
        values: {
          registration_email: /*#__PURE__*/_jsx(Text, {
            text: email,
            bold: true
          })
        }
      })]
    }), /*#__PURE__*/_jsx(InputText, {
      name: "password",
      placeholder: translate('placeholder'),
      validation: errorMessage,
      type: isPasswordVisible ? InputText.Type.Text : InputText.Type.Password,
      onChange: handleInputChange,
      value: password,
      suffix: /*#__PURE__*/_jsx(Button, {
        size: Button.Size.Medium,
        icon: /*#__PURE__*/_jsx(Icon, {
          name: passwordIconName,
          color: Icon.Color.Gray3_v2,
          size: Icon.Size.Small
        }),
        styling: Button.Styling.Flat,
        onClick: togglePasswordVisibility
      })
    }), /*#__PURE__*/_jsx(Cell, {
      children: /*#__PURE__*/_jsx("div", {
        className: "u-text-center",
        children: /*#__PURE__*/_jsxs(SeparatedList, {
          separator: /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.XLarge
          }),
          children: [/*#__PURE__*/_jsx(Button, {
            testId: "login-button",
            onClick: doAuthenticate,
            text: translate('button', {
              registration_email: email
            }),
            styling: Button.Styling.Filled
          }), renderPasswordResetLink(FORGOT_PASSWORD_URL), /*#__PURE__*/_jsx(FaqEntryUrl, {
            type: FaqEntryType.CantLogin,
            accessChannel: AccessChannel.ProductLink,
            render: url => renderContactUsLink(url)
          })]
        })
      })
    }), /*#__PURE__*/_jsx(Spacer, {})]
  });

  return isLoginWithoutEmailInModalEnabled ? renderContent() : /*#__PURE__*/_jsx(Card, {
    children: renderContent()
  });
};

export default PasswordVerificationLogin;